import { Check, Info, Warning, X, type IconGlyph } from "@/icons";

export type FeedbackVariant = "success" | "error" | "warning" | "info";

/** Feedback variants plus the quiet `neutral` used by inline callouts. */
export type FeedbackTone = FeedbackVariant | "neutral";

type FeedbackVariantConfig = {
  Icon: IconGlyph;
  /** Icon color */
  accentText: string;
  /** Soft background for the icon badge / banner */
  tintBg: string;
  /** Slightly stronger tint used by the animated halo behind the badge */
  haloBg: string;
  /** Border that pairs with tintBg on banners */
  border: string;
  /** Full-tone border of a blocking notice (`Callout emphasis="strong"`) */
  borderStrong: string;
};

/**
 * Each tone in THREE strengths of the SAME color: full on the icon, 10% on the
 * chip, 20% on the halo and stroke. Derived by opacity, so dark palettes do not
 * get a pale blotch. Warning uses the fixed `warning` token.
 */
export const FEEDBACK_VARIANTS: Record<FeedbackTone, FeedbackVariantConfig> = {
  success: {
    Icon: Check,
    accentText: "text-secondary",
    tintBg: "bg-secondary/10",
    haloBg: "bg-secondary/20",
    border: "border-secondary/20",
    borderStrong: "border-secondary",
  },
  error: {
    Icon: X,
    accentText: "text-primary-red",
    tintBg: "bg-primary-red/10",
    haloBg: "bg-primary-red/20",
    border: "border-primary-red/20",
    borderStrong: "border-primary-red",
  },
  warning: {
    Icon: Warning,
    accentText: "text-warning",
    tintBg: "bg-warning/10",
    haloBg: "bg-warning/20",
    border: "border-warning/40",
    borderStrong: "border-warning",
  },
  info: {
    Icon: Info,
    accentText: "text-primary",
    tintBg: "bg-primary/10",
    haloBg: "bg-primary/20",
    border: "border-primary/20",
    borderStrong: "border-primary",
  },
  neutral: {
    Icon: Info,
    accentText: "text-card-text/75",
    tintBg: "bg-gray-scale-100",
    haloBg: "bg-gray-scale-200",
    border: "border-border",
    borderStrong: "border-card-text/40",
  },
};
